import { Component, Input, Output, EventEmitter, OnInit, forwardRef, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { AppConfigService } from '../../appconfig/appconfig.service';
import { FileUrlPipe, FileUrlChannelContext } from '../../pipes/file-url.pipe';
import { Observable } from 'rxjs';

export interface ImageUploadConfig {
  uploadUrl: string;
  maxSizeBytes?: number;
  allowedTypes?: string[];
  minWidth?: number;
  minHeight?: number;
  requireSquare?: boolean;
  uploadButtonText?: string;
  dropText?: string;
  requirementsText?: string;
  /** When true, the file is read client-side as a base64 data URI instead of uploading to the server. */
  clientSideOnly?: boolean;
}

export interface ImageUploadResult {
  url: string;
  originalUrl?: string;
  iconUrl?: string;
}

@Component({
  standalone: true,
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, FileUrlPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true
    }
  ]
})
export class ImageUploadComponent implements OnInit, ControlValueAccessor {
  @Input() config: ImageUploadConfig;
  @Input() currentImageUrl: string = ''; // Deprecated - kept for backwards compatibility
  @Input() disabled: boolean = false;
  @Input() previewSize: number = 150;
  /**
   * Channel routing context used to render the preview. The api host only serves stored
   * file paths via the anonymous /files/by-channel/{type}/{uniqueId}/{container}/{id} route
   * (Bearer can't ride cross-origin <img> requests). Pass the channel that owns the file —
   * for bot-level visuals, the bot's signalr channel.
   */
  @Input() channelContext: FileUrlChannelContext | null = null;
  @Output() uploadComplete = new EventEmitter<ImageUploadResult>();
  @Output() uploadError = new EventEmitter<string>();

  public uploading: boolean = false;
  public uploadProgress: number = 0;
  public dragOver: boolean = false;
  public imageTimestamp: number = Date.now();
  public readonly inputId: string = `imageFileInput_${Math.random().toString(36).slice(2)}`;

  // ControlValueAccessor implementation
  private _value: string = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  private defaultConfig: Partial<ImageUploadConfig> = {
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
    minWidth: 64,
    minHeight: 64,
    requireSquare: false,
    uploadButtonText: 'Upload Image',
    // Short: this is now the label INSIDE a 150px overlay on the image, not the
    // instruction in a wide drop zone, and .image-edit hides overflow. (CHAT-995)
    dropText: 'Change Image',
    requirementsText: 'PNG, JPEG, or GIF format'
  };

  // Standalone pipe instance — manually instantiated (the pipe is pure/stateless, so a per-
  // component `new` is cheaper than wiring DI for a single transform). Used by the previewSrc
  // getter instead of running the pipe in the template, so we can detect "pipe returned empty"
  // and fall back to the default placeholder rather than emit a broken relative URL.
  private readonly _fileUrlPipe = new FileUrlPipe();

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.config = { ...this.defaultConfig, ...this.config };
    // Support backwards compatibility with currentImageUrl input
    if (this.currentImageUrl && !this._value) {
      this._value = this.currentImageUrl;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this._value = value || '';
    this.imageTimestamp = Date.now();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get value(): string {
    // Support backwards compatibility - use currentImageUrl if value not set
    return this._value || this.currentImageUrl || '';
  }

  /**
   * Resolved <img src> for the preview. Falls back to the placeholder when:
   *  - no value (nothing uploaded yet), or
   *  - the FileUrlPipe returns empty (missing channel context — e.g. multi-tenant Teams
   *    parent channel with no ChannelUniqueID — or missing api base config).
   * The previous template ternary only checked `value` truthiness, so an empty pipe result
   * concatenated with the cache-buster produced a relative URL like "?t=12345" that the
   * browser rendered as a broken image.
   */
  get previewSrc(): string {
    const DEFAULT = 'assets/img/crchat-logo-square.png';
    const v = this.value;
    if (!v) return DEFAULT;
    if (v.startsWith('data:')) return v;

    const resolved = this._fileUrlPipe.transform(v, this.channelContext);
    if (!resolved) return DEFAULT;

    const sep = resolved.includes('?') ? '&' : '?';
    return `${resolved}${sep}t=${this.imageTimestamp}`;
  }

  onDragOver(event: DragEvent) {
    if (this.disabled || this.uploading) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  /**
   * Normalize an upload-response URL into the bare "{container}/{fileId}" form we store.
   * Data URIs pass through unchanged (post-upload preview from FileReader).
   * Same-origin route-shaped relative URLs (`/files/by-channel/...` or `/files/...`) get
   * their `{container}/{fileId}` tail extracted so they don't get persisted with the routing
   * prefix baked in.
   * Absolute URLs (legacy `https://host/files/...` or new `https://host/files/by-channel/...`)
   * are stripped the same way so the stored value stays environment-portable — the api base
   * + by-channel routing gets re-applied at render time by the FileUrlPipe.
   * Anything else (already bare, or an external URL we don't recognize) is returned as-is.
   */
  private toBareStoredPath(value: string | undefined | null): string {
    if (!value) return '';
    if (value.startsWith('data:')) return value;

    // Strip any "?query" or "#fragment" suffix off a candidate stored path. Cache-busters
    // (e.g. "?t=12345") and version hints (e.g. "?v=1") in upload responses must NOT be
    // persisted into the stored value — they'd never match an actual blob name on read.
    const stripQueryAndFragment = (s: string): string => s.split('?')[0].split('#')[0];

    // Pull the routing/legacy prefixes off of a pathname-only string. Used for both the
    // relative-URL branch below and the absolute-URL branch's parsed pathname.
    const stripRoutingPrefix = (pathname: string): string | null => {
      const byChannel = pathname.match(/\/files\/by-channel\/[^/]+\/[^/]+\/(.+)$/i);
      if (byChannel?.[1]) return byChannel[1];
      const legacy = pathname.match(/\/files\/(.+)$/i);
      if (legacy?.[1]) return legacy[1];
      return null;
    };

    if (!/^https?:\/\//i.test(value)) {
      // Same-origin relative responses (e.g. some legacy endpoints) come back as `/files/...`
      // — extract the tail before falling back to the "already bare" trim so we don't persist
      // the routing prefix as if it were the container name.
      const cleaned = stripQueryAndFragment(value);
      if (cleaned.startsWith('/')) {
        const stripped = stripRoutingPrefix(cleaned);
        if (stripped) return stripped;
        return cleaned.substring(1);
      }
      return cleaned;
    }
    try {
      const u = new URL(value);
      // URL parsing already separates pathname from search/hash, so query/fragment are
      // implicitly dropped here.
      const stripped = stripRoutingPrefix(u.pathname);
      if (stripped) return stripped;
    } catch {
      // Malformed URL — fall through and return as-is so it stays visible in error scenarios
      // rather than getting silently dropped.
    }
    return value;
  }

  onDrop(event: DragEvent) {
    if (this.disabled || this.uploading) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileUpload(files[0]);
    }
  }

  onFileSelected(event: Event) {
    if (this.disabled || this.uploading) return;
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.handleFileUpload(target.files[0]);
    }
  }

  triggerFileInput() {
    if (this.disabled || this.uploading) return;
    const input = document.getElementById(this.inputId) as HTMLInputElement;
    input?.click();
  }

  private handleFileUpload(file: File) {
    // Validate file type
    if (!this.config.allowedTypes.includes(file.type)) {
      const error = `Only ${this.config.allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')} files are allowed`;
      this.toastr.error(error, 'Invalid File Type');
      this.uploadError.emit(error);
      return;
    }

    // Validate file size
    if (file.size > this.config.maxSizeBytes) {
      const error = `File size must be less than ${Math.round(this.config.maxSizeBytes / 1024 / 1024)}MB`;
      this.toastr.error(error, 'File Too Large');
      this.uploadError.emit(error);
      return;
    }

    // Validate image dimensions if required
    this.validateImageDimensions(file).subscribe({
      next: (isValid) => {
        if (!isValid) {
          let error = `Image must be at least ${this.config.minWidth}x${this.config.minHeight} pixels`;
          if (this.config.requireSquare) {
            error = `Image must be square and at least ${this.config.minWidth}x${this.config.minHeight} pixels`;
          }
          this.toastr.error(error, 'Invalid Dimensions');
          this.uploadError.emit(error);
          return;
        }

        if (this.config.clientSideOnly) {
          this.readAsBase64(file);
        } else {
          this.uploadImage(file);
        }
      },
      error: () => {
        this.toastr.error('Error validating image', 'Validation Failed');
        this.uploadError.emit('Error validating image');
      }
    });
  }

  private validateImageDimensions(file: File): Observable<boolean> {
    return new Observable(observer => {
      const img = new Image();
      img.onload = () => {
        const meetsMinSize = img.width >= this.config.minWidth && img.height >= this.config.minHeight;
        const isSquare = !this.config.requireSquare || img.width === img.height;
        observer.next(meetsMinSize && isSquare);
        observer.complete();
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        observer.error('Failed to load image');
      };
      img.src = URL.createObjectURL(file);
    });
  }

  private readAsBase64(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;

      this._value = dataUrl;
      this.onChange(dataUrl);
      this.onTouched();
      this.currentImageUrl = dataUrl;
      this.imageTimestamp = Date.now();

      this.uploadComplete.emit({ url: dataUrl });
      this.toastr.success('Image loaded successfully', 'Image Ready');
    };
    reader.onerror = () => {
      this.toastr.error('Failed to read image file', 'Read Failed');
      this.uploadError.emit('Failed to read image file');
    };
    reader.readAsDataURL(file);
  }

  private uploadImage(file: File) {
    this.uploading = true;
    this.uploadProgress = 0;

    const formData = new FormData();
    formData.append('file', file);

    const apiUrl = AppConfigService.settings.get('chatstyleapiServer');
    const uploadUrl = `${apiUrl}${this.config.uploadUrl}`;

    this.http.post<any>(uploadUrl, formData, {
      reportProgress: true,
      observe: 'events'
    }).subscribe({
      next: (event) => {
        if (event.type === 1) { // UploadProgress
          this.uploadProgress = Math.round(100 * event.loaded / event.total);
        } else if (event.type === 4) { // Response
          this.uploading = false;
          this.uploadProgress = 0;

          // Server is supposed to return bare "{container}/{fileId}" paths post-migration, but
          // not every legacy upload endpoint has been audited — and any that still emits a full
          // absolute URL would write host-bound values straight back into SQL, breaking the
          // environment-portable storage contract. Normalize defensively so the stored value
          // is always a bare path regardless of which endpoint produced it.
          let result: ImageUploadResult;
          if (event.body.profilePicUrl) {
            result = { url: this.toBareStoredPath(event.body.profilePicUrl) };
          } else if (event.body.avatarUrl) {
            result = {
              url: this.toBareStoredPath(event.body.avatarUrl),
              originalUrl: event.body.originalUrl ? this.toBareStoredPath(event.body.originalUrl) : undefined,
              iconUrl: event.body.iconUrl ? this.toBareStoredPath(event.body.iconUrl) : undefined
            };
          } else {
            result = { url: this.toBareStoredPath(event.body.url) };
          }

          // Update ControlValueAccessor value with the bare path
          this._value = result.url;
          this.onChange(result.url);
          this.onTouched();

          this.currentImageUrl = result.url;
          this.imageTimestamp = Date.now();

          this.uploadComplete.emit(result);
          this.toastr.success('Image uploaded successfully', 'Upload Complete');
        }
      },
      error: (error) => {
        this.uploading = false;
        this.uploadProgress = 0;
        
        let errorMessage = 'Failed to upload image';
        if (error.status === 0) {
          errorMessage = 'Cannot connect to server. Please check if the API is running.';
        } else if (error.status === 404) {
          errorMessage = 'Upload endpoint not found. Please check the API configuration.';
        } else if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error && error.error.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        this.toastr.error(errorMessage, 'Upload Failed');
        this.uploadError.emit(errorMessage);
      }
    });
  }
}